import React, { Fragment, useState } from 'react';
import { Button, CircularProgress, Typography, TextField, IconButton, InputAdornment } from '@mui/material';
import { Box, Container, Stack, Grid } from '../imports/muiImport';
import { Helmet } from 'react-helmet-async';
import { GoogleLogin } from '@react-oauth/google';

import { useNavigate } from '../imports/generalImport';
import { registerUserWithGoogle, loginUser, changePassword, verifyOtp } from '../apiCalls/urlFunction';
import { userTokenSave } from '../apiCalls/apiHandler';
import { actionNotifier } from '../components/tostify';
import { useAuth } from '../hooks/contexts/AuthContext';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import loginimage from '../assets/99giftimages/Loginpage1.jpeg';
import { jwtDecode } from "jwt-decode";
import { toast } from 'react-toastify';
import OtpInput from "react18-input-otp";
import { Loading } from 'notiflix';


const AuthLayout = () => {
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verifyOTPKey, setVerifyOTPKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [error, setError] = useState("")

  const [userProcessStatus, setUserProcessStatus] = useState({
    currentStep: "LOGIN_OR_SIGNUP",
  })

  Loading.init({
    svgColor: '#002865',
    messageColor: '#000',
    backgroundColor: 'rgba(255,255,255,0.8)',
    clickToClose: false,
  });

  // Form inputs state management
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [otp, setOtp] = useState("");
  const [newPasswordData, setNewPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const { setIsLoggedIn } = useAuth();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    try {
      setLoading(true);
      Loading.hourglass('Please wait...');

      setError("");

      const response = await loginUser(formData);

      if (response?.status) {
        setUserProcessStatus({
          currentStep: "OTP",
        });

        setVerifyOTPKey(response?.data);
      } else {
        setError("Invalid Email Or Password")
      }
    } catch (error) {
      setError("Invalid Email Or Password");

    } finally {
      Loading.remove();
      setLoading(false);
    }
  };
  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    try {
      const payload = {
        idToken: credentialResponse.credential,
      };

      const response = await registerUserWithGoogle(payload);

      if (response?.status && response?.data) {
        if (isSignup) {
          // Sign Up flow: Clear user data (no token saved) and redirect to login
          actionNotifier.success(response?.message || 'Google signup successful! Please log in.', { position: 'top-left', autoClose: 2000 });
        } else {
          // Login flow
          const decodedPayload = jwtDecode(response.data);
          const tokenSaved = userTokenSave(response.data);

          if (decodedPayload?.passwordExists) {
            if (tokenSaved) {
              setIsLoggedIn(true);
              window.location.reload();
              setTimeout(() => {
                navigate('/', { replace: true });
              }, 300);
            }
          } else {
            setUserProcessStatus({ currentStep: "NEW_PASSWORD" });
          }
        }
      } else {
        actionNotifier.error(response?.message || 'Google authentication failed', { position: 'top-left', autoClose: 3000 });
      }
    } catch (error) {
      console.error('Google auth error:', error);
      actionNotifier.error('Something went wrong during Google authentication', { position: 'top-left', autoClose: 3000 });
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setGoogleLoading(false);
    actionNotifier.error(
      'Google authentication was cancelled or failed',
      { position: 'top-left', autoClose: 2000 }
    );
  };

  const isSignup = window.location.pathname === '/signup';

  const FormDesign = () => {
    return (
      <Fragment>
        {/* Header */}
        <Stack
          spacing={1.5}
          sx={{
            mb: { xs: 3, sm: 3.5 },
            width: '100%',
            alignItems: 'flex-start'
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: '#000',
              fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
              fontFamily: "Be Vietnam Pro",
              letterSpacing: '-0.5px',
            }}
          >
            {isSignup ? 'Create Account' : 'Welcome Back'}
          </Typography>

          <Typography
            sx={{
              fontWeight: 400,
              color: '#666666',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontFamily: "Be Vietnam Pro",
              lineHeight: 1.5,
            }}
          >
            {isSignup
              ? 'Sign up and start sending joy with eGifts cards'
              : 'Login to explore and send joy with eGifts cards'}
          </Typography>
        </Stack>

        {/* Google Login */}
        <Stack sx={{ position: 'relative', width: '100%', mb: 2 }}>
          {googleLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
                borderRadius: '12px',
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            theme="outline"
            size="large"
            text={isSignup ? 'signup_with' : 'signin_with'}
            width="100%"
            logo_alignment="center"
            shape="pill"
          />
        </Stack>

        {!isSignup && (
          <Fragment>
            {/* Divider */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                my: 1.5,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  borderBottom: '1px solid #e8e8e8',
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  mx: 2,
                  color: '#999999',
                  fontFamily: "'Be Vietnam Pro', sans-serif",
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                or continue with
              </Typography>
            </Box>
            {error && (
              <Typography
                sx={{
                  mt: 2,
                  mb: 2,
                  m: 'auto',
                  fontFamily: "Be Vietnam Pro",
                  fontWeight: 500,
                  color: "error.main"

                }}
              >
                {error}

              </Typography>
            )}


            {/* Form */}
            <Box component="form" onSubmit={handleFormSubmit} sx={{ width: '100%' }}>
              <Stack spacing={2}>
                {/* Email Field */}
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      mb: 0.75,
                      textAlign: 'left',
                      color: '#000',
                      fontFamily: "Be Vietnam Pro",
                      fontSize: '1rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    Email Address
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter your email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    variant="outlined"
                    type="email"
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ pl: 0.5, pr: 0.5 }}>
                          <Mail size={18} color="#999" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f5f6f8',
                        borderRadius: '10px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#f0f1f4',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 0 0 3px rgba(0,0,0,0.05)',
                        },
                        '& fieldset': {
                          border: '1px solid transparent',
                          transition: 'all 0.2s ease',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d0d0d0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#000000',
                          borderWidth: '1px',
                        },
                        '& input': {
                          py: 1.7,
                          fontSize: '0.9rem',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Password Field */}
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      mb: 0.75,
                      textAlign: 'left',
                      color: '#000',
                      fontFamily: "Be Vietnam Pro",
                      fontSize: '1rem',
                      letterSpacing: '0.3px',
                    }}
                  >
                    Password
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter your password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ pl: 0.5, pr: 0.5 }}>
                          <Lock size={18} color="#999" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{
                              color: 'primary.main',
                              '&:hover': { backgroundColor: 'rgba(250, 84, 28, 0.05)' },
                            }}
                          >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f5f6f8',
                        borderRadius: '10px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#f0f1f4',
                        },
                        '&.Mui-focused': {
                          backgroundColor: '#ffffff',
                          boxShadow: '0 0 0 3px rgba(0,0,0,0.05)',
                        },
                        '& fieldset': {
                          border: '1px solid transparent',
                          transition: 'all 0.2s ease',
                        },
                        '&:hover fieldset': {
                          borderColor: '#d0d0d0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#000000',
                          borderWidth: '1px',
                        },
                        '& input': {
                          py: 1.7,
                          fontSize: '0.9rem',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Submit Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: '#ffffff',
                    borderRadius: '10px',
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '1.2rem',
                    boxShadow: 'none',
                    transition: 'all 0.3s ease',
                    mt: 0.5,
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={24}
                      color="inherit"
                      thickness={5}
                    />
                  ) : (
                    isSignup ? 'Create Account' : 'Login'
                  )}
                </Button>
              </Stack>
            </Box>
          </Fragment>
        )}


      </Fragment>
    )
  }

  const OTPDesign = () => {
    // const handleOtpChange = (element, index) => {
    //   if (isNaN(element.value)) return false;
    //   const newOtp = [...otp];
    //   newOtp[index] = element.value;
    //   setOtp(newOtp);

    //   if (element.value !== '' && element.nextSibling) {
    //     element.nextSibling.focus();
    //   }
    // };

    const handleVerifyOtp = async (otp) => {
      try {
        setLoading(true);
        Loading.hourglass('Please wait...');
        setOtpError("");

        const res = await verifyOtp({
          key: verifyOTPKey,
          otp,
        });

        if (res?.status) {
          setOtpError("");

          const decodedPayload = jwtDecode(res?.data);

          if (decodedPayload?.passwordExists) {
            const tokenSaved = userTokenSave(res.data);

            if (tokenSaved) {
              setIsLoggedIn(true);
              navigate("/login", { replace: true });
            } else {
              setOtpError("Login Failed");
            }
          }
        } else {
          setOtpError(res?.message || "Invalid OTP");
        }
      } catch (error) {
        setOtpError(
          error?.response?.data?.message ||
          error?.message ||
          "Invalid OTP"
        );
      } finally {
        Loading.remove();
        setLoading(false);

      }
    };
    const handleOtpSubmit = (e) => {
      e.preventDefault();
      if (loading) return;
      const otpValue = otp;

      if (otpValue.length < 6) {
        toast.error('Please enter 6 digit OTP');
        return;
      }

      handleVerifyOtp(otpValue);
    };

    return (
      <Fragment>
        <Stack spacing={1.5} sx={{ mb: { xs: 3, sm: 3.5 }, width: '100%', alignItems: 'flex-start' }}>
          <Typography sx={{ fontWeight: 700, color: '#000', fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }, fontFamily: "Be Vietnam Pro", letterSpacing: '-0.5px' }}>
            Verify OTP
          </Typography>
          <Typography
            sx={{
              color: otpError ? "error.main" : "#666666",
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontFamily: "Be Vietnam Pro",
              lineHeight: 1.5,
              fontWeight: otpError ? 600 : "none"
            }}
          >
            {otpError
              ? "Invalid OTP. Please enter a valid OTP."
              : "Please enter the 6-digit code sent to your email."}
          </Typography>
        </Stack>

        <Box component="form" onSubmit={handleOtpSubmit} sx={{ width: '100%' }}>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <OtpInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                isInputNum
                shouldAutoFocus
                separator={<span style={{ width: "8px" }} />}
                inputStyle={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "10px",
                  border: "1px solid #d0d5dd",
                  backgroundColor: "#f5f6f8",
                  fontSize: "1.5rem",
                  fontWeight: 600,
                  color: "#000",
                  textAlign: "center",
                  outline: "none",
                }}
                focusStyle={{
                  border: "1px solid #000",
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || otp?.length !== 6}
              sx={{
                backgroundColor: 'primary.main',
                color: '#ffffff',
                borderRadius: '10px',
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1.2rem',
                boxShadow: 'none',
                mt: 1,
                '&:hover': { backgroundColor: 'primary.main', transform: 'translateY(-1px)' }
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Verify OTP'
              )}
            </Button>
          </Stack>
        </Box>
      </Fragment>
    );
  }

  const NewPasswordDesign = () => {
    const handleNewPasswordChange = (e) => {
      const { name, value } = e.target;
      setNewPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const handleChangePassword = async (payload) => {
      try {
        const response = await changePassword({
          "password": String(payload)
        });

        if (response?.status) {
          toast.success(response?.message);
          navigate('/');
        } else {
          toast.error(response?.message);
        }
      } catch (error) {
        setError("Please Try Again - Change Password!");
      }
    };

    const handleNewPasswordSubmit = async (e) => {
      e.preventDefault();

      setError("");

      if (!newPasswordData.password) {
        setError("Password is required");
        return;
      }


      if (newPasswordData.password) {
        handleChangePassword(newPasswordData?.password)
      }
    };

    return (
      <Fragment>
        <Stack spacing={1.5} sx={{ mb: { xs: 3, sm: 3.5 }, width: '100%', alignItems: 'flex-start' }}>
          <Typography sx={{ fontWeight: 700, color: '#000', fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }, fontFamily: "Be Vietnam Pro", letterSpacing: '-0.5px' }}>
            Set New Password
          </Typography>
          <Typography
            sx={{
              fontWeight: error ? 600 : 400,
              color: error ? 'error.main' : '#666666',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontFamily: "Be Vietnam Pro",
              lineHeight: 1.5
            }}
          >
            {error || "Please enter your new password below."}
          </Typography>
        </Stack>

        <Box component="form" onSubmit={handleNewPasswordSubmit} sx={{ width: '100%' }}>
          <Stack spacing={2}>
            {/* New Password Field */}
            <Box>
              <Typography sx={{ fontWeight: 500, mb: 0.75, textAlign: 'left', color: '#000', fontFamily: "Be Vietnam Pro", fontSize: '1rem' }}>
                New Password
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter new password"
                name="password"
                value={newPasswordData.password}
                onChange={handleNewPasswordChange}
                type={showNewPassword ? 'text' : 'password'}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={{ pl: 0.5, pr: 0.5 }}><Lock size={18} color="#999" /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" sx={{ color: 'primary.main' }}>
                        {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f6f8',
                    borderRadius: '10px',
                    '&.Mui-focused fieldset': { borderColor: '#000000', borderWidth: '1px' }
                  }
                }}
              />
            </Box>

            {/* Confirm Password Field */}
            <Box>
              <Typography sx={{ fontWeight: 500, mb: 0.75, textAlign: 'left', color: '#000', fontFamily: "Be Vietnam Pro", fontSize: '1rem' }}>
                Confirm Password
              </Typography>
              <TextField
                fullWidth
                placeholder="Confirm new password"
                name="confirmPassword"
                value={newPasswordData.confirmPassword}
                onChange={handleNewPasswordChange}
                type={showConfirmPassword ? 'text' : 'password'}
                required
                InputProps={{
                  startAdornment: <InputAdornment position="start" sx={{ pl: 0.5, pr: 0.5 }}><Lock size={18} color="#999" /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{ color: 'primary.main' }}>
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#f5f6f8',
                    borderRadius: '10px',
                    '&.Mui-focused fieldset': { borderColor: '#000000', borderWidth: '1px' }
                  }
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: 'primary.main',
                color: '#ffffff',
                borderRadius: '10px',
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1.2rem',
                boxShadow: 'none',
                mt: 1,
                '&:hover': { backgroundColor: 'primary.main', transform: 'translateY(-1px)' }
              }}
            >
              Set Password
            </Button>
          </Stack>
        </Box>
      </Fragment>
    );
  }

  const renderDesignHandler = () => {
    switch (userProcessStatus.currentStep) {
      case "LOGIN_OR_SIGNUP":
        return FormDesign();
      case "OTP":
        return OTPDesign();
      case "NEW_PASSWORD":
        return NewPasswordDesign();
      default:
        return FormDesign();
    }
  }



  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        minHeight: '100vh',
        display: 'flex',
        backgroundColor: '#fafafa',
        position: 'relative',
        p: {
          lg: 1,
        }
      }}
    >
      <Helmet>
        <title>{isSignup ? 'Signup - 99gift.ae' : 'Login - 99gift.ae'}</title>
        <meta property="og:title" content={isSignup ? 'Signup - 99gift.ae' : 'Login - 99gift.ae'} />
      </Helmet>

      <Grid container
        sx={{
          width: '100%',
          flex: 1,
          backgroundColor: 'transparent',
        }}
      >
        {/* Left Side: Forms */}

        <Grid item xs={12} md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: '#ffffff',
            height: '100%',
            overflowY: 'auto',
            p: { xs: 2, sm: 4, md: 6 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              maxWidth: '400px',
              width: '100%',
              mx: 'auto',
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src="https://www.99gift.in/static/media/logo.84c3804906626862f8f077a5d0acd071.svg"
              alt="99gift.ae"
              onClick={() => navigate("/")}
              sx={{
                height: { xs: 55, sm: 65, md: 75 },
                objectFit: "contain",
                mb: { xs: 2.5, sm: 3.5 },
                alignSelf: "center",
                cursor: "pointer",
              }}
            />
            {renderDesignHandler()}

          </Box>
        </Grid>

        {/* Right Side: Image */}
        <Grid item xs={12} md={6}
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'relative',
            height: '100%',
            // overflow: 'hidden',
            py: {
              md: 2,
            }
          }}
        >
          <Box
            component="img"
            src={loginimage}
            alt="Auth Visual"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              borderRadius: 4,
            }}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default AuthLayout;
